import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ScatterChart, Scatter } from 'recharts';

const MiningReturns = ({ stopeID }) => {
  const BASE_URL = 'https://pickstonepresentation.dumisaminerals.com/server/api';
  const [miningReturnsData, setMiningReturnsData] = useState([]);
  const [productionReturnsData, setProductionReturnsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productionLoading, setProductionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [productionCurrentPage, setProductionCurrentPage] = useState(1);
  const [productionItemsPerPage] = useState(10);
  const [xAxis, setXAxis] = useState('DateDeveloped');
  const [yAxis, setYAxis] = useState('HolesDrilled');
  const [chartType, setChartType] = useState('bar');
  const [productionXAxis, setProductionXAxis] = useState('DateDeveloped');
  const [productionYAxis, setProductionYAxis] = useState('HolesDrilled');
  const [productionChartType, setProductionChartType] = useState('bar');

  // Pagination logic
  const totalPages = Math.ceil(miningReturnsData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = miningReturnsData.slice(startIndex, endIndex);

  // Production Returns pagination logic
  const productionTotalPages = Math.ceil(productionReturnsData.length / productionItemsPerPage);
  const productionStartIndex = (productionCurrentPage - 1) * productionItemsPerPage;
  const productionEndIndex = productionStartIndex + productionItemsPerPage;
  const productionCurrentData = productionReturnsData.slice(productionStartIndex, productionEndIndex);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [miningReturnsData]);

  useEffect(() => {
    setProductionCurrentPage(1);
  }, [productionReturnsData]);

  // Format date for better readability
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };

  // Get all columns for X axis
  const getAllColumns = () => {
    if (miningReturnsData.length === 0) return [];
    const excludedFields = ['StopeID', 'EndID', 'HolesDrilled', 'HolesCharged', 'HolesDepth', 
                            'ExplosivesEmex', 'ExplosivesEZStoppers', 'ExplosivesIED', 'ExplosivesIGCord',
                            'PreviousAdvance', 'EstimatedExplosives', 'EstimatedTonnage', 'EstimatedPowderFactor'];
    const availableFields = Object.keys(miningReturnsData[0]).filter(field => !excludedFields.includes(field));
    
    const preferredOrder = [
      'DateDeveloped',
      'Month',
      'MachineOperator',
      'MachineAssistant', 
      'TeamLeader',
      'Comments',
      'Planned'
    ];
    
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Always add Month as it's a computed field
    if (!orderedFields.includes('Month')) {
      orderedFields.splice(1, 0, 'Month'); // Insert after DateDeveloped
    }
    
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Get numeric columns for Y axis
  const getNumericColumns = () => {
    const numericFields = ['HolesDrilled', 'HolesCharged', 'HolesDepth', 'ExplosivesEmex', 
                           'ExplosivesEZStoppers', 'ExplosivesIED', 'ExplosivesIGCord',
                           'PreviousAdvance', 'EstimatedExplosives', 'EstimatedTonnage', 'EstimatedPowderFactor'];
    return numericFields.filter(field => miningReturnsData.some(item => !isNaN(parseFloat(item[field]))));
  };

  // Get all columns for Production Returns X axis
  const getProductionAllColumns = () => {
    if (productionReturnsData.length === 0) return [];
    const excludedFields = ['STOPEID', 'HolesDrilled', 'HolesCharged', 'HolesDepth', 
                            'ExplosivesEmex', 'ExplosivesEZStoppers', 'ExplosivesIED', 'ExplosivesIGCord',
                            'BenchLength', 'BenchWidth', 'EstimatedTonnage', 'EstimatedPowderFactor', 
                            'EstimatedExplosives', 'BenchArea', 'TonnesBroken'];
    const availableFields = Object.keys(productionReturnsData[0]).filter(field => !excludedFields.includes(field));
    
    const preferredOrder = [
      'DateDeveloped',
      'Month',
      'MachineOperator',
      'MachineAssistant', 
      'TeamLeader',
      'Comments',
      'Planned'
    ];
    
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Always add Month as it's a computed field
    if (!orderedFields.includes('Month')) {
      orderedFields.splice(1, 0, 'Month'); // Insert after DateDeveloped
    }
    
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Get numeric columns for Production Returns Y axis
  const getProductionNumericColumns = () => {
    const numericFields = ['HolesDrilled', 'HolesCharged', 'HolesDepth', 'ExplosivesEmex', 
                           'ExplosivesEZStoppers', 'ExplosivesIED', 'ExplosivesIGCord',
                           'BenchLength', 'BenchWidth', 'EstimatedTonnage', 'EstimatedPowderFactor', 
                           'EstimatedExplosives', 'BenchArea', 'TonnesBroken'];
    return numericFields.filter(field => productionReturnsData.some(item => !isNaN(parseFloat(item[field]))));
  };

  // Process data for chart
  const processChartData = () => {
    if (!miningReturnsData.length) return [];

    const grouped = {};
    const originalDates = {};
    
    miningReturnsData.forEach(item => {
      let key = item[xAxis];
      let originalDate = null;
      
      if (xAxis === 'DateDeveloped') {
        originalDate = item[xAxis];
        key = formatDate(key);
      } else if (xAxis === 'Month') {
        // Create Month field from DateDeveloped
        const date = new Date(item.DateDeveloped);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          key = `${month}_${year}`;
        } else {
          key = 'Invalid Date';
        }
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [xAxis]: key,
          count: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      getNumericColumns().forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value)) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    const processedData = Object.values(grouped).map(group => {
      const processed = { [xAxis]: group[xAxis], count: group.count };
      
      getNumericColumns().forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          // Use sum instead of average
          processed[field] = parseFloat(total.toFixed(2));
          processed[`${field}_total`] = total;
        }
      });
      
      return processed;
    });

    if (xAxis === 'DateDeveloped') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[xAxis]]);
        const dateB = new Date(originalDates[b[xAxis]]);
        return dateA - dateB;
      });
    }

    if (xAxis === 'Month') {
      return processedData.sort((a, b) => {
        // Sort by year first, then by month
        const [monthA, yearA] = a[xAxis].split('_');
        const [monthB, yearB] = b[xAxis].split('_');
        const yearCompare = parseInt(yearA) - parseInt(yearB);
        if (yearCompare !== 0) return yearCompare;
        return parseInt(monthA) - parseInt(monthB);
      });
    }

    return processedData;
  };

  const chartData = processChartData();

  // Process data for Production Returns chart
  const processProductionChartData = () => {
    if (!productionReturnsData.length) return [];

    const grouped = {};
    const originalDates = {};
    
    productionReturnsData.forEach(item => {
      let key = item[productionXAxis];
      let originalDate = null;
      
      if (productionXAxis === 'DateDeveloped') {
        originalDate = item[productionXAxis];
        key = formatDate(key);
      } else if (productionXAxis === 'Month') {
        // Create Month field from DateDeveloped
        const date = new Date(item.DateDeveloped);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          key = `${month}_${year}`;
        } else {
          key = 'Invalid Date';
        }
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [productionXAxis]: key,
          count: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      getProductionNumericColumns().forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value)) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    const processedData = Object.values(grouped).map(group => {
      const processed = { [productionXAxis]: group[productionXAxis], count: group.count };
      
      getProductionNumericColumns().forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          // Use sum instead of average
          processed[field] = parseFloat(total.toFixed(2));
          processed[`${field}_total`] = total;
        }
      });
      
      return processed;
    });

    if (productionXAxis === 'DateDeveloped') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[productionXAxis]]);
        const dateB = new Date(originalDates[b[productionXAxis]]);
        return dateA - dateB;
      });
    }

    if (productionXAxis === 'Month') {
      return processedData.sort((a, b) => {
        // Sort by year first, then by month
        const [monthA, yearA] = a[productionXAxis].split('_');
        const [monthB, yearB] = b[productionXAxis].split('_');
        const yearCompare = parseInt(yearA) - parseInt(yearB);
        if (yearCompare !== 0) return yearCompare;
        return parseInt(monthA) - parseInt(monthB);
      });
    }

    return processedData;
  };

  const productionChartData = processProductionChartData();

  // Filter mining returns data by stopeID
  const filterMiningReturnsByStope = (data) => {
    if (!stopeID || !data.length) return data;
    
    return data.filter(item => {
      if (!item.StopeID) return false;
      
      const databaseStopeID = item.StopeID;
      const parameterStopeID = stopeID;
      
      // Check if database StopeID is at least 5 characters long and contains "L_"
      if (databaseStopeID.length < 5 || !databaseStopeID.includes('L_')) {
        return false;
      }
      
      if (parameterStopeID.startsWith(databaseStopeID)) {
        const nextChar = parameterStopeID.charAt(databaseStopeID.length);
        if (!nextChar || isNaN(parseInt(nextChar))) {
          return true;
        }
      }
      
      return false;
    });
  };

  useEffect(() => {
    if (stopeID) {
      setLoading(true);

      const fetchMiningReturnsData = async () => {
        try {
          const response = await fetch(`${BASE_URL}/developmentdrillingreturn`);
          if (!response.ok) throw new Error('Failed to fetch development drilling data');
          
          const result = await response.json();
          const filteredData = filterMiningReturnsByStope(result);
          
          console.log('Development drilling - Original data count:', result.length);
          console.log('Development drilling - Filtered data count:', filteredData.length);
          
          setMiningReturnsData(filteredData);
        } catch (err) {
          console.error('Error fetching development drilling data:', err);
          setMiningReturnsData([]);
        } finally {
          setLoading(false);
        }
      };

      fetchMiningReturnsData();
    }
  }, [stopeID]);

  // Fetch production returns data
  useEffect(() => {
    if (stopeID) {
      setProductionLoading(true);

      const fetchProductionReturnsData = async () => {
        try {
          const response = await fetch(`${BASE_URL}/productiondrillingreturn`);
          if (!response.ok) throw new Error('Failed to fetch production drilling data');
          
          const result = await response.json();
          const filteredData = filterMiningReturnsByStope(result);
          
          console.log('Production drilling - Original data count:', result.length);
          console.log('Production drilling - Filtered data count:', filteredData.length);
          
          setProductionReturnsData(filteredData);
        } catch (err) {
          console.error('Error fetching production drilling data:', err);
          setProductionReturnsData([]);
        } finally {
          setProductionLoading(false);
        }
      };

      fetchProductionReturnsData();
    }
  }, [stopeID]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-2 text-gray-600">Loading development drilling data...</span>
      </div>
    );
  }


  // Calculate summary statistics for each drilling type
  const calculateDrillingSummaryStats = (data, isProduction = false) => {
    if (!data || data.length === 0) {
      return {
        dateRange: 'N/A',
        holesDrilled: 0,
        holesCharged: 0,
        holesDepth: 0,
        explosivesEmex: 0,
        explosivesEZStoppers: 0,
        explosivesIED: 0,
        explosivesIGCord: 0,
        machineOperator: 0,
        benchLength: isProduction ? 0 : 'N/A',
        benchWidth: isProduction ? 0 : 'N/A',
        estimatedTonnage: 0,
        estimatedPowderFactor: 0,
        estimatedExplosives: 0,
        benchArea: isProduction ? 0 : 'N/A',
        tonnesBroken: isProduction ? 0 : 'N/A'
      };
    }

    const dates = data.map(item => new Date(item.DateDeveloped)).filter(date => !isNaN(date.getTime()));
    
    // Calculate sums for numeric fields
    const holesDrilled = data.reduce((sum, item) => sum + (parseFloat(item.HolesDrilled) || 0), 0);
    const holesCharged = data.reduce((sum, item) => sum + (parseFloat(item.HolesCharged) || 0), 0);
    const holesDepth = data.reduce((sum, item) => sum + (parseFloat(item.HolesDepth) || 0), 0);
    const explosivesEmex = data.reduce((sum, item) => sum + (parseFloat(item.ExplosivesEmex) || 0), 0);
    const explosivesEZStoppers = data.reduce((sum, item) => sum + (parseFloat(item.ExplosivesEZStoppers) || 0), 0);
    const explosivesIED = data.reduce((sum, item) => sum + (parseFloat(item.ExplosivesIED) || 0), 0);
    const explosivesIGCord = data.reduce((sum, item) => sum + (parseFloat(item.ExplosivesIGCord) || 0), 0);
    const estimatedTonnage = data.reduce((sum, item) => sum + (parseFloat(item.EstimatedTonnage) || 0), 0);
    const estimatedPowderFactor = data.reduce((sum, item) => sum + (parseFloat(item.EstimatedPowderFactor) || 0), 0);
    const estimatedExplosives = data.reduce((sum, item) => sum + (parseFloat(item.EstimatedExplosives) || 0), 0);
    
    // Production-specific fields
    const benchLength = isProduction ? data.reduce((sum, item) => sum + (parseFloat(item.BenchLength) || 0), 0) : 'N/A';
    const benchWidth = isProduction ? data.reduce((sum, item) => sum + (parseFloat(item.BenchWidth) || 0), 0) : 'N/A';
    const benchArea = isProduction ? data.reduce((sum, item) => sum + (parseFloat(item.BenchArea) || 0), 0) : 'N/A';
    const tonnesBroken = isProduction ? data.reduce((sum, item) => sum + (parseFloat(item.TonnesBroken) || 0), 0) : 'N/A';
    
    // Get count of unique machine operators
    const operators = [...new Set(data.map(item => item.MachineOperator).filter(op => op))];
    const machineOperator = operators.length;
    
    return {
      dateRange: dates.length > 0 ? `${formatDate(new Date(Math.min(...dates)))} to ${formatDate(new Date(Math.max(...dates)))}` : 'N/A',
      holesDrilled,
      holesCharged,
      holesDepth,
      explosivesEmex,
      explosivesEZStoppers,
      explosivesIED,
      explosivesIGCord,
      machineOperator,
      benchLength,
      benchWidth,
      estimatedTonnage,
      estimatedPowderFactor,
      estimatedExplosives,
      benchArea,
      tonnesBroken
    };
  };

  const developmentStats = calculateDrillingSummaryStats(miningReturnsData, false);
  const productionStats = calculateDrillingSummaryStats(productionReturnsData, true);

  return (
    <div className="space-y-4">
      {/* Summary Statistics Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 p-3 border-b border-gray-200">Summary Statistics</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Metric</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Development Drilling</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Production Drilling</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Date Range</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.dateRange}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.dateRange}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Holes Drilled</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.holesDrilled}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.holesDrilled}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Holes Charged</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.holesCharged}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.holesCharged}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Holes Depth</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.holesDepth.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.holesDepth.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Explosives Emex</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.explosivesEmex.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.explosivesEmex.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Explosives EZ Stoppers</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.explosivesEZStoppers.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.explosivesEZStoppers.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Explosives IED</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.explosivesIED.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.explosivesIED.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Explosives IG Cord</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.explosivesIGCord.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.explosivesIGCord.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Machine Operator Count</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.machineOperator}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.machineOperator}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Bench Length</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.benchLength}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.benchLength.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Bench Width</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.benchWidth}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.benchWidth.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Estimated Tonnage</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.estimatedTonnage.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.estimatedTonnage.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Estimated Powder Factor</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.estimatedPowderFactor.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.estimatedPowderFactor.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Estimated Explosives</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.estimatedExplosives.toFixed(2)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.estimatedExplosives.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Bench Area</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.benchArea}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.benchArea.toFixed(2)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Tonnes Broken</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{developmentStats.tonnesBroken}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{productionStats.tonnesBroken.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Development Drilling Table */}
      {miningReturnsData && miningReturnsData.length > 0 && (
        <>
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Development Drilling Data</h3>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Date Developed</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">End ID</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Holes Drilled</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Holes Charged</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Holes Depth</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machine Operator</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">
                      {formatDate(item.DateDeveloped)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.EndID || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.HolesDrilled || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.HolesCharged || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.HolesDepth || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">{item.MachineOperator || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {miningReturnsData.length > itemsPerPage && (
            <div className="flex items-center justify-center space-x-2 p-2 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                title="First page"
              >
                |&lt;
              </button>
              
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                title="Previous page"
              >
                &lt;
              </button>
              
              <span className="text-xs text-gray-600 px-2">
                {currentPage} / {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                title="Next page"
              >
                &gt;
              </button>
              
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                title="Last page"
              >
                &gt;|
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Interactive Graph Section */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Development Drilling Graph</h3>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">X Axis</label>
                <select 
                  value={xAxis} 
                  onChange={(e) => setXAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {getAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Y Axis</label>
                <select 
                  value={yAxis} 
                  onChange={(e) => setYAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="count">Count</option>
                  {getNumericColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
                <select 
                  value={chartType} 
                  onChange={(e) => setChartType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setXAxis('DateDeveloped');
                    setYAxis('HolesDrilled');
                    setChartType('bar');
                  }}
                  className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Chart Container */}
            <div style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'bar' && (
                  <BarChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={yAxis} fill="#8b5cf6" />
                  </BarChart>
                )}
                
                {chartType === 'line' && (
                  <LineChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey={yAxis} stroke="#8b5cf6" strokeWidth={2} />
                  </LineChart>
                )}
                
                {chartType === 'scatter' && (
                  <ScatterChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Scatter dataKey={yAxis} fill="#8b5cf6" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Production Drilling Section */}
      {productionReturnsData && productionReturnsData.length > 0 && (
        <>
        <div className="mt-8">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Production Drilling Data</h3>
        
        {productionLoading ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
            <span className="ml-2 text-gray-600">Loading production drilling data...</span>
          </div>
        ) : productionReturnsData.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Date</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">HolesDrilled</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">HolesCharged</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">HolesDepth</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">ExplosivesEmex</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">ExplosivesEZStoppers</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">ExplosivesIED</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">ExplosivesIGCord</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">MachineOperator</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">BenchLength</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">BenchWidth</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">EstimatedTonnage</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">EstimatedPowderFactor</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">EstimatedExplosives</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">BenchArea</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">TonnesBroken</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">MachineAssistant</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Comments</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Planned</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {productionCurrentData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{formatDate(item.DateDeveloped)}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.HolesDrilled || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.HolesCharged || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.HolesDepth || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.ExplosivesEmex || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.ExplosivesEZStoppers || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.ExplosivesIED || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.ExplosivesIGCord || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.MachineOperator || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.BenchLength || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.BenchWidth || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.EstimatedTonnage || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.EstimatedPowderFactor || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.EstimatedExplosives || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.BenchArea || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.TonnesBroken || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.MachineAssistant || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.Comments || 'N/A'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">{item.Planned || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Production Returns Pagination Controls */}
            {productionReturnsData.length > productionItemsPerPage && (
              <div className="flex items-center justify-center space-x-2 p-2 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setProductionCurrentPage(1)}
                  disabled={productionCurrentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="First page"
                >
                  |&lt;
                </button>
                
                <button
                  onClick={() => setProductionCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={productionCurrentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Previous page"
                >
                  &lt;
                </button>
                
                <span className="text-xs text-gray-600 px-2">
                  {productionCurrentPage} / {productionTotalPages}
                </span>
                
                <button
                  onClick={() => setProductionCurrentPage(prev => Math.min(prev + 1, productionTotalPages))}
                  disabled={productionCurrentPage === productionTotalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Next page"
                >
                  &gt;
                </button>
                
                <button
                  onClick={() => setProductionCurrentPage(productionTotalPages)}
                  disabled={productionCurrentPage === productionTotalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Last page"
                >
                  &gt;|
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-gray-500">
            <p>No production drilling data available for this stope.</p>
          </div>
        )}
      </div>

      {/* Production Drilling Graph */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Production Drilling Graph</h3>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">X-Axis</label>
                <select
                  value={productionXAxis}
                  onChange={(e) => setProductionXAxis(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                >
                  {getProductionAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Y-Axis</label>
                <select
                  value={productionYAxis}
                  onChange={(e) => setProductionYAxis(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                >
                  {getProductionNumericColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Chart Type</label>
                <select
                  value={productionChartType}
                  onChange={(e) => setProductionChartType(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
            </div>

            {/* Chart */}
            <div className="w-full" style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {productionChartType === 'bar' && (
                  <BarChart 
                    data={productionChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={productionXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={productionYAxis} fill="#10b981" />
                  </BarChart>
                )}
                
                {productionChartType === 'line' && (
                  <LineChart 
                    data={productionChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={productionXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey={productionYAxis} stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                )}
                
                {productionChartType === 'scatter' && (
                  <ScatterChart 
                    data={productionChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={productionXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Scatter dataKey={productionYAxis} fill="#10b981" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default MiningReturns;
